import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUpRight, ExternalLink, Plus, ReceiptText, Trash2 } from 'lucide-react';
import { gauntlet, ApiError, type PaymentRequest, type RequestStatus } from '../lib/api';
import { cx, formatAmount, formatFiat, timeAgo } from '../lib/format';
import { Badge, Button, Callout, CopyButton, Input, Modal, Panel, Skeleton } from '../components/ui';

/**
 * Payment requests — shareable, trackable "pay me" links.
 *
 * Single mode is one fixed invoice; multi mode is a goal many people pay
 * toward. The public link works for anyone, no account needed, and the
 * status flips live the moment the mempool sweep sees a payment.
 */
export function RequestsPage() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['requests'],
    queryFn: () => gauntlet.requests(),
    refetchInterval: 30_000,
  });

  const { data: walletsData } = useQuery({
    queryKey: ['wallets'],
    queryFn: () => gauntlet.wallets(),
  });

  const cancel = useMutation({
    mutationFn: (id: number) => gauntlet.cancelRequest(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => gauntlet.deleteRequest(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });

  const confirmDelete = (row: PaymentRequest): void => {
    if (
      window.confirm(
        `Delete "${row.title}"? This removes the request and its link permanently. ` +
          'The address stays yours — it just stops being tracked by a request.',
      )
    ) {
      remove.mutate(row.id);
    }
  };

  const requests = data?.requests ?? [];

  if (isLoading) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="inline-flex items-center gap-2 text-xl font-semibold tracking-tight">
            <ReceiptText size={19} className="text-brass" />
            Payment requests
          </h1>
          <p className="mt-1 text-sm text-ink-dim">
            Ask for money with a link. Works for anyone, no account needed —
            the status goes live the moment a payment is broadcast.
          </p>
        </div>
        <Button variant="primary" icon={<Plus size={14} />} onClick={() => setCreating(true)}>
          New request
        </Button>
      </div>

      {requests.length === 0 ? (
        <Panel>
          <p className="py-8 text-center text-sm text-ink-faint">
            No requests yet. Create one and share the link — "0.001 BTC for the Pi setup" and
            the money lands in your wallet.
          </p>
        </Panel>
      ) : (
        <div className="space-y-3">
          {requests.map(({ status, ...row }) => (
            <RequestCard
              key={row.id}
              row={row as PaymentRequest}
              status={status as RequestStatus}
              onCancel={() => cancel.mutate(row.id)}
              cancelling={cancel.isPending && cancel.variables === row.id}
              onDelete={() => confirmDelete(row as PaymentRequest)}
              deleting={remove.isPending && remove.variables === row.id}
            />
          ))}
        </div>
      )}

      <Callout tone="info" title="How this works">
        Each request gets its own freshly derived address — never the one your Receive page
        shows. When someone pays it from any wallet, Gauntlet sees the broadcast within a
        minute and the link flips to paid, even before it confirms. Only pay people you know.
        Developers: <a href="/docs" target="_blank" rel="noreferrer noopener" className="font-medium text-brass no-underline hover:underline">API docs</a>.
      </Callout>

      <NewRequestModal
        open={creating}
        onClose={() => setCreating(false)}
        accounts={walletsData?.wallets.flatMap((w) =>
          w.accounts.map((a) => ({ id: a.id, chainId: a.chain_id, walletId: w.id, walletName: w.name })),
        ) ?? []}
      />
    </div>
  );
}

function RequestCard({
  row,
  status,
  onCancel,
  cancelling,
  onDelete,
  deleting,
}: {
  row: PaymentRequest;
  status: RequestStatus;
  onCancel: () => void;
  cancelling: boolean;
  onDelete: () => void;
  deleting: boolean;
}) {
  const publicUrl = `${window.location.origin}/r/${row.slug}`;
  const goal = BigInt(row.amount);
  const confirmed = BigInt(status.confirmedTotal);
  const progress = goal > 0n ? Math.min(100, Number((confirmed * 100n) / goal)) : 0;
  const stale = row.status === 'open' && status.status !== 'open' ? status.status : row.status;

  return (
    <Panel padded={false}>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-medium">{row.title}</span>
            <Badge>{row.mode === 'single' ? 'invoice' : 'goal'}</Badge>
            <StatusChip status={stale} />
            {row.expiresAt && (
              <span className="text-[11px] text-ink-faint">
                expires {new Date(row.expiresAt).toLocaleDateString()}
              </span>
            )}
          </div>
          {row.note && <p className="mt-0.5 truncate text-xs text-ink-dim">{row.note}</p>}
          <p className="num mt-0.5 text-xs text-ink-faint">
            {formatAmount(row.amount, 8)} {symbolFor(row.chainId)} · created {timeAgo(row.createdAt)}
          </p>
        </div>

        <div className="shrink-0 text-right">
          {row.mode === 'multi' ? (
            <>
              <p className="num text-sm font-medium">
                {formatAmount(status.confirmedTotal, 8)} / {formatAmount(row.amount, 8)}{' '}
                {symbolFor(row.chainId)}
              </p>
              <div className="mt-1 h-1.5 w-32 overflow-hidden rounded-full bg-raised">
                <div
                  className="h-full rounded-full bg-brass transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </>
          ) : (
            <p className="num text-sm font-medium">
              {formatAmount(row.amount, 8)} {symbolFor(row.chainId)}
            </p>
          )}
          {status.pendingTotal !== '0' && (
            <p className="mt-0.5 text-[11px] text-brass">
              {formatAmount(status.pendingTotal, 8)} pending — unconfirmed
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <CopyButton value={publicUrl} label="Copy" />
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="rounded-lg border border-hairline p-2 text-ink-dim transition-colors hover:border-hairline-bright hover:text-ink"
            title="Open public page"
          >
            <ArrowUpRight size={14} />
          </a>
          {row.status === 'open' && (
            <Button size="sm" variant="ghost" loading={cancelling} onClick={onCancel}>
              Cancel
            </Button>
          )}
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            className="rounded-lg border border-hairline p-2 text-ink-faint transition-colors hover:border-loss/40 hover:text-loss disabled:opacity-50"
            title="Delete request"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </Panel>
  );
}

function StatusChip({ status }: { status: string }) {
  const tone =
    status === 'paid' || status === 'completed'
      ? 'border-gain/40 bg-gain/10 text-gain'
      : status === 'open'
        ? 'border-hairline text-ink-dim'
        : 'border-loss/30 bg-loss/10 text-loss';
  return (
    <span className={cx('rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize', tone)}>
      {status}
    </span>
  );
}

function symbolFor(chainId: string): string {
  const map: Record<string, string> = {
    bitcoin: 'BTC', 'bitcoin-testnet': 'tBTC', 'bitcoin-signet': 'sBTC',
    ethereum: 'ETH', litecoin: 'LTC', dogecoin: 'DOGE', solana: 'SOL',
    monero: 'XMR', cosmos: 'ATOM',
  };
  return map[chainId] ?? chainId.slice(0, 4).toUpperCase();
}

/* ------------------------------------------------------------------ create */

function NewRequestModal({
  open,
  onClose,
  accounts,
}: {
  open: boolean;
  onClose: () => void;
  accounts: { id: number; chainId: string; walletId: number; walletName: string }[];
}) {
  const queryClient = useQueryClient();
  const [accountId, setAccountId] = useState<number | ''>('');
  const [mode, setMode] = useState<'single' | 'multi'>('single');
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState('');
  const [expiryDays, setExpiryDays] = useState<number | ''>('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<PaymentRequest | null>(null);

  const account = accounts.find((a) => a.id === accountId);

  const { data: portfolio } = useQuery({
    queryKey: ['portfolio'],
    queryFn: () => gauntlet.portfolio(),
    staleTime: 60_000,
  });
  const currency = portfolio?.currency ?? 'usd';

  const { data: priceData } = useQuery({
    queryKey: ['price', account?.chainId ?? '', currency],
    queryFn: () =>
      gauntlet.prices([account?.chainId === 'bitcoin' ? 'bitcoin' : (account?.chainId ?? '')], currency),
    enabled: Boolean(account) && account?.chainId === 'bitcoin',
    staleTime: 60_000,
  });
  const price = priceData?.prices['bitcoin']?.price ?? null;

  const create = useMutation({
    mutationFn: () =>
      gauntlet.createRequest({
        accountId: Number(accountId),
        mode,
        title,
        note: note || undefined,
        chainId: account?.chainId ?? '',
        amount: toBaseUnits(amount, 8),
        ...(expiryDays ? { expiryDays: Number(expiryDays) } : {}),
        ...(webhookUrl.trim() && webhookSecret.trim()
          ? { webhookUrl: webhookUrl.trim(), webhookSecret: webhookSecret.trim() }
          : {}),
      }),
    onSuccess: async (result) => {
      setCreated(result.request);
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : 'Could not create the request.'),
  });

  const publicUrl = created ? `${window.location.origin}/r/${created.slug}` : '';
  const fiatHint = useMemo(() => {
    if (!price || !amount || !(Number(amount) > 0)) return null;
    const base = toBaseUnits(amount, 8);
    const [w = '0', f = ''] = price.split('.');
    const scaledPrice = BigInt(w + f.slice(0, 12).padEnd(12, '0'));
    const cents = (BigInt(base) * scaledPrice) / 10n ** BigInt(8 + 12 - 2);
    return formatFiat(cents.toString(), currency);
  }, [price, amount, currency]);

  const reset = (): void => {
    setCreated(null);
    setTitle(''); setNote(''); setAmount(''); setExpiryDays('');
    setWebhookUrl(''); setWebhookSecret(''); setError(null);
    setMode('single');
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        onClose();
        reset();
      }}
      title={created ? 'Request created' : 'New payment request'}
      width="max-w-lg"
    >
      {created ? (
        <div className="space-y-4">
          <Callout tone="success" title="Your link is ready">
            Share it anywhere — anyone with the link can pay, no account needed.
          </Callout>
          <div className="rounded-lg border border-hairline bg-abyss p-3">
            <p className="addr break-all text-xs">{publicUrl}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <CopyButton value={publicUrl} label="Copy link" />
            <a
              href={publicUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1.5 rounded-lg border border-hairline px-3 py-2 text-sm text-ink-dim transition-colors hover:border-hairline-bright hover:text-ink"
            >
              <ExternalLink size={13} /> Open page
            </a>
            <Button
              variant="ghost"
              onClick={() => {
                reset();
              }}
            >
              Create another
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <label className="block">
            <span className="label-xs">Account</span>
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value ? Number(e.target.value) : '')}
              className="mt-1 h-10 w-full rounded-lg border border-hairline bg-abyss px-3 text-sm"
            >
              <option value="">Choose an account…</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.walletName} · {a.chainId}
                </option>
              ))}
            </select>
          </label>

          <div className="flex rounded-lg border border-hairline p-1">
            {(
              [
                ['single', 'One payment'],
                ['multi', 'A goal (many people)'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setMode(id)}
                className={cx(
                  'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  mode === id ? 'bg-brass-wash text-brass' : 'text-ink-dim hover:text-ink',
                )}
              >
                {label}
              </button>
            ))}
          </div>

          <Input
            label={mode === 'single' ? 'Title' : 'What are you raising for?'}
            placeholder={mode === 'single' ? '0.001 BTC for the Pi setup' : 'Help me fix my laptop'}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={80}
          />
          <Input
            label="Note (optional)"
            placeholder="What is this for? Where does the money go?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
          />
          <Input
            label={mode === 'single' ? 'Amount' : 'Goal amount'}
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
            placeholder="0.001"
            inputMode="decimal"
            mono
            suffix={account ? account.chainId.toUpperCase() : ''}
          />
          {fiatHint && <p className="num -mt-2 text-xs text-ink-faint">≈ {fiatHint}</p>}

          <label className="block">
            <span className="label-xs">Expires after (optional)</span>
            <select
              value={expiryDays}
              onChange={(e) => setExpiryDays(e.target.value ? Number(e.target.value) : '')}
              className="mt-1 h-10 w-full rounded-lg border border-hairline bg-abyss px-3 text-sm"
            >
              <option value="">Never</option>
              <option value={1}>1 day</option>
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </select>
          </label>

          <details className="rounded-lg border border-hairline p-3">
            <summary className="cursor-pointer text-[13px] font-medium text-ink-dim">
              Developer — webhook notifications
            </summary>
            <div className="mt-3 space-y-3">
              <p className="text-xs leading-relaxed text-ink-faint">
                When the request is paid, Gauntlet POSTs a signed JSON payload to your URL.
                Set both fields or neither.
              </p>
              <Input
                label="Webhook URL"
                placeholder="https://your-site.com/payment/notify"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
              />
              <Input
                label="Webhook secret"
                placeholder="a long random string, 16+ chars"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
              />
            </div>
          </details>

          {error && <Callout tone="danger">{error}</Callout>}

          <Button
            variant="primary"
            full
            disabled={
              !accountId ||
              !title.trim() ||
              !(Number(amount) > 0)
            }
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Create request
          </Button>
        </div>
      )}
    </Modal>
  );
}

/** Decimal string → base units, without touching floating point. */
function toBaseUnits(value: string, decimals: number): string {
  const [whole = '0', frac = ''] = value.split('.');
  const padded = frac.slice(0, decimals).padEnd(decimals, '0');
  return `${BigInt(whole || '0') * 10n ** BigInt(decimals) + BigInt(padded || '0')}`.replace(
    /^0+(\d)/,
    '$1',
  );
}
